import {getHalAddressMode} from "#webgl2/utils"
import {WebGl2Settings} from "#webgl2/settings"
import type {HalContext, HalPainter, HalPainterImageBlitPaintArgs, ShaderReturnType, HalPainterImageCompositorRef} from "@cm/hal"
import type {Size2Like} from "@cm/math"
import {Box2, Vector2} from "@cm/math"

export class WebGl2PainterImageBlit implements HalPainter {
    constructor(
        readonly context: HalContext,
        blittingFunction?: string,
        returnType?: ShaderReturnType,
    ) {
        blittingFunction ??= DEFAULT_BLITTING_FUNCTION

        WebGl2PainterImageBlit.instances?.add(this)

        returnType ??= "vec4"
        this.imageCompositor = this.context.createHalPainterImageCompositor(this.getCompositingFunction(blittingFunction, returnType), returnType)
    }

    dispose(): void {
        if (!this.imageCompositor) {
            throw new Error("WebGl2PainterImageBlit: Already disposed")
        }
        this.imageCompositor.release()
        this.imageCompositor = null

        WebGl2PainterImageBlit.instances?.delete(this)
    }

    paint({target, parameters, sourceImages, options}: HalPainterImageBlitPaintArgs) {
        if (!this.imageCompositor) {
            throw new Error("WebGl2PainterImageBlit: Already disposed")
        }
        const primarySource = sourceImages instanceof Array ? sourceImages[0] : sourceImages
        const primarySourceSize: Size2Like = primarySource.descriptor
        const sourceRegion = options?.sourceRegion
            ? Box2.fromBox2Like(options?.sourceRegion)
            : Box2.fromPositionAndSize(
                  {
                      x: 0,
                      y: 0,
                  },
                  new Vector2(primarySourceSize.width, primarySourceSize.height),
              )
        const targetOffset = options?.targetOffset ?? {x: 0, y: 0}
        const addressMode = options?.addressMode ?? "border"
        this.imageCompositor.ref.paint({
            target,
            parameters: {
                ...parameters,
                // A custom blitting function may not use all of these, and the shader compiler drops unused uniforms.
                u_internal_blitSourceOffset: {
                    type: "int2",
                    value: new Vector2(sourceRegion.x - targetOffset.x, sourceRegion.y - targetOffset.y),
                    isOptional: true,
                },
                u_sourceMipLevel: {type: "int", value: options?.sourceMipLevel ?? 0, isOptional: true},
                u_addressMode: {type: "int", value: getHalAddressMode(addressMode), isOptional: true},
            },
            sourceImages,
            options: {
                ...options,
                targetRegion: Box2.fromPositionAndSize(targetOffset, sourceRegion.size),
            },
        })
    }

    private getCompositingFunction(blittingFunction: string, returnType: ShaderReturnType): string {
        return `
            ${blittingFunction}
            
            uniform ivec2 u_internal_blitSourceOffset;
            uniform int u_sourceMipLevel;
            uniform int u_addressMode;

            ${returnType} computeColor(ivec2 targetPixel) {
                return computeColor(targetPixel, targetPixel + u_internal_blitSourceOffset, u_sourceMipLevel, u_addressMode);
            }
        `
    }

    public static create(context: HalContext, compositingFunction?: string): WebGl2PainterImageBlit {
        return new WebGl2PainterImageBlit(context, compositingFunction)
    }

    private imageCompositor: HalPainterImageCompositorRef | null

    static instances = WebGl2Settings.InstanceTracking ? new Set<WebGl2PainterImageBlit>() : undefined
}

const DEFAULT_BLITTING_FUNCTION = `
    vec4 computeColor(ivec2 targetPixel, ivec2 sourcePixel, int lod, int addressMode) {
        return texelFetchLod0(sourcePixel, lod, addressMode);
    }
`
